import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'antd'
import { compose } from 'redux'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import _ from 'lodash'

import CoreRenderFunction from 'DisplayCore'
import { buildRewardProgramsPageLayout } from 'PageLayout/Wallet/RewardPrograms'
import LoadingOverlay from 'components/Indicator/LoadingOverlay'
import { getSelectedLanguageContent } from 'utils'

import WithRewardPrograms from './actions'

const FREQUENCY_OPTIONS = [
  { id: 'monthly', value: 'monthly', label: 'Monthly' },
  { id: 'weekly', value: 'weekly', label: 'Weekly' }
]

const buildProgramKey = value => String( value ?? '' )
  .toLowerCase()
  .replace( /[^a-z0-9]+/g, '_' )
  .replace( /^_+|_+$/g, '' )
  .slice( 0, 50 )

const toDateString = value => value ? dayjs( value ).format( 'YYYY-MM-DD' ) : null

const RewardPrograms = props => {
  const modulePermissions = props.modulePermissions

  const {
    query,
    getRewardPrograms,
    getRewardProgram,
    createRewardProgram,
    updateRewardProgram,
    deleteRewardProgram,
    onChangeRewardProgramsHOC
  } = props

  const [ detailForm ] = Form.useForm()
  const [ action, setAction ] = useState( null )
  const [ selectedProgram, setSelectedProgram ] = useState({})
  const [ programToDelete, setProgramToDelete ] = useState({})
  const [ isProgramKeyEdited, setIsProgramKeyEdited ] = useState( false )

  const queryRef = useRef( query )
  queryRef.current = query

  const pageLayout = useMemo(() => buildRewardProgramsPageLayout({
    canUpdate: modulePermissions.can_update,
    onToggleActive: ( program, active ) => updateRewardProgram( program.id, {
      name: program.name,
      description: program.description,
      start_date: program.start_date,
      end_date: program.end_date,
      active
    })
  }), [])

  useEffect(() => {
    if ( !action ) {
      setSelectedProgram({})
      setIsProgramKeyEdited( false )
      detailForm.resetFields()
    }
  }, [ action ])

  useEffect(() => {
    if ( modulePermissions.can_read ) {
      getRewardPrograms()
    }
  }, [ query ])

  const onColumnFilterChanged = useCallback( _.debounce( ( column, value ) => {
    const current = queryRef.current
    const filter = { ...current.filter }

    if ( value ) {
      filter[ `${ column }__contains` ] = value
    } else {
      delete filter[ `${ column }__contains` ]
    }

    if ( !_.isEqual( filter, current.filter ) ) {
      onChangeRewardProgramsHOC( 'query', { ...current, filter, page: 1 })
    }
  }, 500 ), [])

  const onUpdateTableInfo = ( key, value ) => {
    const current = queryRef.current

    switch ( key ) {
      case 'page':
        if ( current.page !== value ) {
          onChangeRewardProgramsHOC( 'query', { ...current, page: value })
        }
        break
      case 'pageSize':
        if ( current.page_size !== value ) {
          onChangeRewardProgramsHOC( 'query', { ...current, page_size: value, page: 1 })
        }
        break
      case 'sorter': {
        const sorter = {}
        _.map( value, val => sorter[ val.id ] = val.desc ? 'desc' : 'asc' )
        if ( !_.isEmpty( sorter ) && !_.isEqual( sorter, current.sorter ) ) {
          onChangeRewardProgramsHOC( 'query', { ...current, sorter, page: 1 })
        }
        break
      }
      default:
    }
  }

  const onClickAction = ( actionKey, ...args ) => {
    switch ( actionKey ) {
      case 'OPEN_CREATE_MODAL':
        setAction( 'create' )
        break
      case 'CLOSE_DETAIL_MODAL':
        setAction( null )
        break
      case 'OPEN_UPDATE_MODAL':
        getRewardProgram( args[ 0 ].id, response => {
          setAction( 'update' )
          setSelectedProgram( response )
          detailForm.setFieldsValue({
            reward_type_id: response.reward_type_ids?.[ 0 ],
            program_key: response.id,
            name: response.name,
            description: response.description,
            processing_frequency: response.processing_frequency,
            start_date: response.start_date ? dayjs( response.start_date ) : null,
            end_date: response.end_date ? dayjs( response.end_date ) : null,
            active: response.active
          })
        })
        break
      case 'DETAIL_FORM_CHANGED': {
        const changedValue = args[ 1 ] || {}
        if ( action !== 'create' ) {
          break
        }
        if ( _.has( changedValue, 'program_key' ) ) {
          setIsProgramKeyEdited( true )
        } else if ( _.has( changedValue, 'name' ) && !isProgramKeyEdited ) {
          detailForm.setFieldValue( 'program_key', buildProgramKey( changedValue.name ) )
        }
        break
      }
      case 'OPEN_DELETE_CONFIRMATION':
        setProgramToDelete( args[ 0 ] )
        break
      case 'CLOSE_DELETE_CONFIRMATION':
        setProgramToDelete({})
        break
      case 'CONFIRM_DELETE':
        deleteRewardProgram( programToDelete.id, () => setProgramToDelete({}) )
        break
      case 'UPDATE_TABLE_INFO':
        onUpdateTableInfo( args[ 0 ], args[ 1 ] )
        break
      case 'COLUMN_FILTER_CHANGED':
        onColumnFilterChanged( args[ 0 ], args[ 1 ] )
        break
      default: return
    }
  }

  const hideComponent = actionKey => {
    switch ( actionKey ) {
      case 'noCreatePermission':
        return !modulePermissions.can_create
      case 'noUpdatePermission':
        return !modulePermissions.can_update
      case 'noDeletePermission':
        return !modulePermissions.can_delete
      case 'hideCreate':
        return action === 'create'
      case 'hideUpdate':
        return action === 'update'
      default:
    }
  }

  const disabledComponent = key => key === 'lockedOnUpdate' && action === 'update'

  const onSubmitDetailForm = formData => {
    const startDate = toDateString( formData.start_date )
    const endDate = toDateString( formData.end_date )

    if ( startDate && endDate && startDate > endDate ) {
      return toast.error( getSelectedLanguageContent( '{{END_DATE_CANNOT_BE_BEFORE_START_DATE}}' ) )
    }

    const payload = {
      name: formData.name,
      description: formData.description || null,
      start_date: startDate,
      end_date: endDate,
      active: !!formData.active
    }

    if ( action === 'create' ) {
      createRewardProgram({
        ...payload,
        reward_type_id: formData.reward_type_id,
        program_key: formData.program_key || buildProgramKey( formData.name ),
        processing_frequency: formData.processing_frequency
      }, () => setAction( null ) )
    } else if ( action === 'update' ) {
      updateRewardProgram( selectedProgram.id, payload, () => setAction( null ) )
    }
  }

  return (
    <>
      {
        modulePermissions.can_read && (
          <CoreRenderFunction
            { ...props }
            item={ pageLayout.content }
            detailForm={ detailForm }
            frequencyOptions={ FREQUENCY_OPTIONS }
            programToDelete={ programToDelete }
            onSubmitDetailForm={ onSubmitDetailForm }
            onClickAction={ onClickAction }
            hideComponent={ hideComponent }
            disabledComponent={ disabledComponent }
            isCreate={ action === 'create' }
            isUpdate={ action === 'update' && !_.isEmpty( selectedProgram ) }
          />
        )
      }
      { props.onLoadRewardPrograms && <LoadingOverlay /> }
    </>
  )
}

export default compose(
  WithRewardPrograms
)( RewardPrograms )
